import React, { useEffect, useState } from "react";
import { AdminPanel, Resource } from "../../assets/icons/Icons";
import { useNavigate } from "react-router-dom";
import SidebarButton from "../atoms/buttons/SidebarButton";
import { getRouteSegment } from "../../lib/utils/funcs";
import { mainItems, supportItems } from "../../assets/data/sidebar";
import { useHospDocData } from "../../lib/contexts/HospitalDoctorContext";
import { AdminPanelHover, ResourceHover } from "../../assets/icons/HoverIcons";
import { useUserData } from "../../lib/contexts/UserContext";
import { LiaFilePrescriptionSolid } from "react-icons/lia";
import { MdDownload } from "react-icons/md";
import { useFeatureFlags } from "../../lib/contexts/FeatureFlagContext";
import { useSidebar } from "../../lib/contexts/SidebarContext";
import BrandLogo from "../atoms/BrandLogo";

import { FaRegUserCircle, FaYoutube, FaWhatsapp } from "react-icons/fa";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { gunasheelaHidingDetails } from "../../lib/utils/constants";
import { getWaLeads } from "../../lib/apis/waLeads";
import { format } from "date-fns";

const Sidebar = () => {
  const [isHovered, setIsHovered] = useState("");
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const [waLeadsCount, setWaLeadsCount] = useState(0);

  const navigate = useNavigate();
  const { hospitalID } = useHospDocData();
  const { userData } = useUserData();
  const { prescription, wa_lead_collection_via_dropoffs, queue_v2, appointment_program_enabled } =
    useFeatureFlags();

  useEffect(() => {
    if (
      (userData?.role === "MasterAdmin" || userData?.role === "Trial") &&
      mainItems.filter((e) => e.name === "Resources").length === 0
    ) {
      mainItems.splice(3, 0, {
        name: "Resources",
        icon: <Resource style={"md:w-5 lg:w-6"} />,
        hoverIcon: <ResourceHover style={"md:w-5 lg:w-6"} />,
      });
    }
    if (
      userData?.role === "MasterAdmin" &&
      mainItems.filter((e) => e.name === "Admin Panel").length === 0
    ) {
      mainItems.splice(1, 0, {
        name: "Admin Panel",
        icon: <AdminPanel style={"md:w-5 lg:w-6"} />,
        hoverIcon: <AdminPanelHover style={"md:w-5 lg:w-6"} />,
      });
    }
    if (
      (userData?.role === "MasterAdmin" || userData?.role === "Doctor") &&
      mainItems.filter((e) => e.name === "Analytics").length === 0
    ) {
      mainItems.splice(1, 0, {
        name: "Analytics",
        icon: <AdminPanel style={"md:w-5 lg:w-6"} />,
        hoverIcon: <AdminPanelHover style={"md:w-5 lg:w-6"} />,
      });
    }
    if (
      prescription &&
      (userData?.role === "Doctor" || userData?.role === "MasterAdmin") &&
      mainItems.filter((e) => e.name === "Create Prescription").length === 0
    ) {
      const appointmentsIndex = mainItems.findIndex(
        (item) => item.name === "Appointments"
      );
      const insertIndex = appointmentsIndex !== -1 ? appointmentsIndex + 1 : 3;
      mainItems.splice(insertIndex, 0, {
        name: "Create Prescription",
        icon: <LiaFilePrescriptionSolid className={"w-6 h-6 md:w-6 md:h-6"} />,
        hoverIcon: <LiaFilePrescriptionSolid className={"md:w-6 md:h-6"} />,
      });
    }
    if (
      wa_lead_collection_via_dropoffs &&
      mainItems.filter((e) => e.name === "WA Leads").length === 0
    ) {
      mainItems.push({
        name: "WA Leads",
        icon: <FaWhatsapp className="w-6 h-6 text-green-600" />,
        hoverIcon: <FaWhatsapp className="w-6 h-6 text-green-600" />,
      });
    }
    // MasterAdmin-only: bulk download of past bookings (back-office tool).
    if (
      userData?.role === "MasterAdmin" &&
      mainItems.filter((e) => e.name === "Download Manager").length === 0
    ) {
      mainItems.push({
        name: "Download Manager",
        icon: <MdDownload className="w-6 h-6" />,
        hoverIcon: <MdDownload className="w-6 h-6" />,
      });
    }
  }, [userData, hospitalID, prescription, wa_lead_collection_via_dropoffs]);

  // Fetch open WA leads count
  const fetchWaLeadsCount = async () => {
    if (!hospitalID || !wa_lead_collection_via_dropoffs) return;
    try {
      const dateStr = format(new Date(), "yyyy-MM-dd");
      const res = await getWaLeads(hospitalID, dateStr, 1, 1, "open");
      if (res?.status === 200) {
        const result = res.data?.result || res.data?.data || res.data;
        if (result?.pagination?.totalRecords != null) {
          setWaLeadsCount(result.pagination.totalRecords);
        } else if (result?.count != null) {
          setWaLeadsCount(result.count);
        } else if (Array.isArray(result)) {
          const first = result[0];
          setWaLeadsCount(first?.count || first?.leads?.length || result.length || 0);
        }
      }
    } catch {}
  };

  useEffect(() => {
    fetchWaLeadsCount();
    const interval = setInterval(fetchWaLeadsCount, 15 * 60 * 1000);
    // Listen for updates from WaLeads page
    const handleUpdate = () => fetchWaLeadsCount();
    window.addEventListener("wa-leads-updated", handleUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener("wa-leads-updated", handleUpdate);
    };
  }, [hospitalID, wa_lead_collection_via_dropoffs]);

  // Filter out "Patients" for restricted Receptionists
  const isRestrictedUser =
  userData?.role === "FloorReceptionist" ||
  (userData?.role === "Receptionist" &&
    gunasheelaHidingDetails.includes(hospitalID || ""));

  // queue_v2 on → show Appointments 2.0 and hide the legacy Appointments;
  // off → the reverse (clinic-by-clinic rollout).
  const filteredMainItems = mainItems.filter((item) => {
    if (isRestrictedUser && item.name === "Patients") return false;
    if (!queue_v2 && item.name === "Appointments 2.0") return false;
    if (queue_v2 && item.name === "Appointments") return false;
    // Categories is the program CATALOG (create/edit/deactivate) -- MasterAdmin
    // only. Linking programs to doctors and bookings stays open to every role.
    if (
      item.name === "Categories" &&
      (!appointment_program_enabled || userData?.role !== "MasterAdmin")
    )
      return false;
    return true;
  });

  return (
    <div
      className={`bg-white hidden xl:flex xl:flex-col ${
        isCollapsed ? "w-20" : "w-[17%]"
      } min-h-screen px-2 border-solid border-e-[1px] border-sbBorder h-[100vh] overflow-y-auto side_bar_prescription transition-all duration-300`}
    >
      {/* Expanded → DocTrue logo in the sidebar (old way). Collapsed → the logo
          moves to the top header bar; here we keep only the collapse toggle. */}
      <div
        className={`mt-3 flex items-center ${
          isCollapsed ? "justify-center px-0" : "justify-between px-4"
        }`}
      >
        {!isCollapsed && <BrandLogo logoClassName="w-32 lg:w-40" />}
        <button
          className={`bg-white border border-sbBorder rounded-lg shadow p-1 hover:bg-gray-100 transition-all ${
            isCollapsed ? "w-full h-8" : "w-10 h-9"
          } flex justify-center items-center mt-0 md:mt-2`}
          style={{ transform: isCollapsed ? "rotate(180deg)" : "none" }}
          onClick={() => setIsCollapsed((prev) => !prev)}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>
      </div>
      <div className="mt-8 flex flex-col">
        {!isCollapsed && (
          <p className="ml-5 uppercase font-medium md:text-[9px] lg:text-[11px] mb-1">
            Main
          </p>
        )}
        <div className="flex flex-col">
          {(userData?.role === "Doctor"
            ? filteredMainItems.map((item) =>
                item.name === "Doctors"
                  ? {
                      name: "Profile",
                      icon: <FaRegUserCircle className="w-6 h-6" />,
                      hoverIcon: <FaRegUserCircle className="w-6 h-6" />,
                    }
                  : item
              )
            : filteredMainItems
          ).map((item, index) => (
            <SidebarButton
              key={index}
              index={index}
              isHovered={isHovered}
              setIsHovered={setIsHovered}
              // Display "Appointments" even when this is the v2 entry — the
              // sidebar tag is the only "2.0" cue; the screen still runs v2.
              name={
                item.name === "Appointments 2.0" ? "Appointments" : item.name
              }
              icon={item.icon}
              hoverIcon={item.hoverIcon}
              onPress={() => {
                const routeName =
                  (item as any).route ??
                  item.name.toLowerCase().replace(/[^a-z0-9]/g, "");
                navigate(hospitalID + "/" + routeName);
              }}
              isSelected={
                (item as any).route
                  ? `${getRouteSegment(2)}/${getRouteSegment(3) ?? ""}` ===
                    (item as any).route
                  : getRouteSegment(2) ===
                      item.name.toLowerCase().replace(/[^a-z0-9]/g, "") &&
                    getRouteSegment(3) !== "v2"
              }
              isCollapsed={isCollapsed}
              rightIcon={
                item.name === "WA Leads" && waLeadsCount > 0
                  ? <span className="relative -top-3 -right-2 animate-bounce bg-blue-500 text-white text-[9px] font-bold w-5 h-5 flex items-center justify-center rounded-md shadow-sm">{waLeadsCount}</span>
                  : undefined
              }
            />
          ))}
        </div>
      </div>
      <div className="mt-8 flex flex-col">
        {!isCollapsed && (
          <p className="ml-5 uppercase font-medium md:text-[9px] lg:text-[11px] mb-1">
            Support
          </p>
        )}
        <div className="flex flex-col">
          {supportItems.map((item, index) => (
            <SidebarButton
              key={index}
              index={index}
              isHovered={isHovered}
              setIsHovered={setIsHovered}
              name={item.name}
              icon={item.icon}
              hoverIcon={item.hoverIcon}
              onPress={() => {
                navigate(
                  hospitalID + "/" + item.name.toLowerCase().split(" ")[0]
                );
              }}
              isSelected={
                getRouteSegment(2) === item.name.split(" ")[0].toLowerCase()
              }
              isCollapsed={isCollapsed}
            />
          ))}
        </div>
      </div>

      <a
        href="https://www.youtube.com/playlist?list=PLbQxMkdQD7qDJun4vhI0Drognf0XWu7aY"
        target="_blank"
        rel="noopener noreferrer"
        className={`${isCollapsed ? "flex justify-center" : ""}`}
      >
        <button
          className={`group
             flex items-center justify-center
             font-sans text-lg
             bg-gradient-to-b from-[#0F3CBE]/100 to-[#1a4fd4]/90
             text-white
             py-2 px-5
             rounded-full
             shadow-lg
             mb-6
             mt-3
             transform transition-all duration-300
             hover:-translate-y-1 hover:shadow-lg
             active:scale-95 active:shadow-sm
             ${isCollapsed ? "w-12 h-12 p-0 ml-2" : "ml-3"}`}
        >
          <div
            className={`flex items-center justify-center
               rounded-full
               bg-white/20
               transition-all duration-300
               group-hover:bg-white/50
               ${isCollapsed ? "w-8 h-8 !bg-transparent" : "w-8 h-8 mr-2"}`}
          >
            <svg
              className={`fill-current
          transition-transform duration-500 ease-in-out
          origin-center group-hover:rotate-45 ${
            isCollapsed ? "w-6 h-6" : "w-4 h-4 "
          }`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
            >
              <path d="M0 0h24v24H0z" fill="none" />
              <path d="M1.946 9.315c-.522-.174-.527-.455.01-.634l19.087-6.362c.529-.176.832.12.684.638l-5.454 19.086c-.15.529-.455.547-.679.045L12 14l6-8-8 6-8.054-2.685z" />
            </svg>
          </div>
          {!isCollapsed && (
            <span className="block text-[1rem] font-[Inter] transition-all text-left duration-300">
              Tutorial
            </span>
          )}
        </button>
      </a>

      {/* v2 is active for this clinic — the only on-screen "2.0" cue.
          The Appointments/Queue screens read normally everywhere else. */}
      {queue_v2 && (
        <div
          className={`mt-auto pt-2 pb-4 flex ${
            isCollapsed ? "justify-center" : "px-5"
          }`}
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
            {isCollapsed ? "2.0" : "Queue 2.0"}
          </span>
        </div>
      )}

      {/* {userData?.role === "Trial" && (
        <div>
          <iframe
            className="w-[14%] h-[20%] rounded-xl fixed top-[78%]"
            src="https://www.youtube.com/embed/ou0phHbzz-0?si=c_1f4IoINOEKHybX"
            title="Demo Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </div>
      )} */}
    </div>
  );
};

export default Sidebar;
